import { useMemo, useState } from "react";
import useSWR from "swr";
import { Download } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fetcher } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { BRAND, CLASS_COLORS, downloadUrl, fmtK, pct } from "@/lib/traffic";
import { Kpi, Seg } from "@/components/traffic/Widgets";
import { RangePicker, SiteSelect, isoDay, presetRange } from "@/components/traffic/SiteSelect";

const DIRS = [{ value: "ALL", label: "Both" }, { value: "NB", label: "NB" }, { value: "SB", label: "SB" }];

function ChartTip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border/70 bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold">{label}</div>
      {payload.map((p) => <div key={p.dataKey} className="flex justify-between gap-4"><span style={{ color: p.color }}>{p.name}</span><span className="num">{fmtNum(p.value)}{unit || ""}</span></div>)}
    </div>
  );
}

export default function Analytics() {
  const [preset, setPreset] = useState("today");
  const [range, setRange] = useState(presetRange("today"));
  const [site, setSite] = useState("ALL");
  const [dir, setDir] = useState("ALL");
  const pick = (p, r) => { setPreset(p); setRange(r); };
  const byRange = preset === "today" || preset === "7d" || preset === "month";
  const period = byRange ? `range=${preset}` : `start=${range[0]}&end=${range[1]}`;
  const { data } = useSWR(`/traffic/series?${period}&site=${site}&dir=${dir}`, fetcher, { refreshInterval: 60000 });
  const sumStart = range[0], sumEnd = range[1];
  const { data: sum } = useSWR(`/traffic/summary?start=${sumStart}&end=${sumEnd}&site=${site}&dir=${dir}`, fetcher, { refreshInterval: 60000 });
  const rows = useMemo(() => (data?.points || []).map((p) => ({
    label: p.label, cur: p.cur?.n ?? null, prev: p.prev?.n ?? 0, prev_week: p.prev_week?.n, spd: p.cur?.spd || null, spd_prev: p.prev?.spd || null,
    small: p.cur?.s ?? 0, medium: p.cur?.m ?? 0, large: p.cur?.l ?? 0,
  })), [data]);
  const isToday = preset === "today";
  const curTotal = rows.reduce((a, r) => a + (r.cur || 0), 0);
  const endsToday = isToday || range[1] >= isoDay(0);
  const done = isToday ? rows.filter((r) => r.cur != null).slice(0, -1) : endsToday ? rows.slice(0, -1) : rows;
  const prevTotal = done.reduce((a, r) => a + r.prev, 0);
  const priorComplete = done.length > 0 && done.filter((r) => r.prev > 0).length >= Math.ceil(done.length * 0.8);
  const change = priorComplete ? pct(done.reduce((a, r) => a + (r.cur || 0), 0), prevTotal) : null;
  const cmpLabel = isToday ? "day-on-day" : preset === "7d" ? "week-on-week" : preset === "month" || preset === "last_month" ? "month-on-month" : "vs. previous period";
  const cmpNote = isToday ? " to the same hour" : endsToday ? " to the same day" : "";
  const tickEvery = isToday ? 1 : rows.length <= 7 ? 0 : rows.length <= 31 ? 2 : 6;
  const exportParams = { start: sumStart, end: sumEnd, site, dir };
  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="eyebrow">Traffic analytics</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">Volumes, classes & speed</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RangePicker preset={preset} range={range} onChange={pick} testId="analytics-range" />
          <Seg value={dir} onChange={setDir} options={DIRS} testId="analytics-dir" />
          <SiteSelect value={site} onChange={setSite} testId="analytics-site" />
          <a href={downloadUrl("/reports/traffic", { ...exportParams, format: "xlsx" })} data-testid="export-excel-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"><Download className="h-3.5 w-3.5" /> Excel</a>
          <a href={downloadUrl("/reports/traffic", { ...exportParams, format: "csv" })} data-testid="export-csv-button" className="rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-secondary">CSV</a>
          <a href={downloadUrl("/reports/traffic", { ...exportParams, format: "pdf" })} data-testid="export-pdf-button" className="rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-secondary">PDF</a>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label={`Vehicles · ${data?.labels?.cur || ""}`} value={fmtK(curTotal)} testId="kpi-card-period-vehicles"
          sub={change == null ? `vs. ${data?.labels?.prev || "previous"}: not enough history` : `${change >= 0 ? "+" : ""}${change}% ${cmpLabel}${cmpNote} (${fmtK(prevTotal)})`} tone={change == null ? null : change >= 0 ? "up" : "down"} />
        <Kpi label="Average speed" value={sum ? `${sum.total.spd} km/h` : "—"} sub={sum ? `85th percentile ${sum.s85} km/h` : null} testId="kpi-card-period-speed" />
        <Kpi label="Daily average" value={fmtK(sum?.daily_avg)} sub={sum?.peak_hour != null ? `Peak hour ${String(sum.peak_hour).padStart(2, "0")}:00` : null} testId="kpi-card-daily-avg" />
        <Kpi label="By class" value={sum ? `${Math.round((100 * sum.total.s) / Math.max(1, sum.total.n))}% small` : "—"}
          sub={sum ? `M ${fmtK(sum.total.m)} · L ${fmtK(sum.total.l)}` : null} testId="kpi-card-classes" />
      </div>

      <section className="surface p-6" data-testid="analytics-volume-chart">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold md:text-lg">Vehicles — {data?.labels?.cur} vs {data?.labels?.prev}{isToday ? ` and ${data?.labels?.prev_week}` : ""}</h2>
          <span className="text-xs text-muted-foreground">{cmpLabel}</span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barGap={2} margin={{ left: -10, right: 28 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickEvery} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fmtK} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="cur" name={data?.labels?.cur || "Current"} fill={BRAND.blue} radius={[4, 4, 0, 0]} />
              <Bar dataKey="prev" name={data?.labels?.prev || "Previous"} fill="#94a3b8" radius={[4, 4, 0, 0]} />
              {isToday && <Line type="monotone" dataKey="prev_week" name={data?.labels?.prev_week || "Last week"} stroke={BRAND.red} dot={false} strokeWidth={1.5} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="surface p-6" data-testid="analytics-class-chart">
          <h2 className="mb-4 text-base font-semibold md:text-lg">Vehicle units by classification</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} stackOffset="none" margin={{ left: -10, right: 28 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickEvery} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fmtK} />
                <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--secondary))" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="small" name="Small" stackId="c" fill={CLASS_COLORS.small} />
                <Bar dataKey="medium" name="Medium" stackId="c" fill={CLASS_COLORS.medium} />
                <Bar dataKey="large" name="Large" stackId="c" fill={CLASS_COLORS.large} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="surface p-6" data-testid="analytics-speed-chart">
          <h2 className="mb-4 text-base font-semibold md:text-lg">Average speed (km/h) <span className="text-xs font-normal text-muted-foreground">· axis follows the measured range</span></h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ left: -10, right: 28 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickEvery} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} domain={[(min) => Math.max(0, Math.floor((min - 6) / 5) * 5), (max) => Math.ceil((max + 6) / 5) * 5]} allowDecimals={false} />
                <Tooltip content={<ChartTip unit=" km/h" />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="spd" name={data?.labels?.cur || "Current"} stroke={BRAND.blue} strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="spd_prev" name={data?.labels?.prev || "Previous"} stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <section className="surface overflow-hidden" data-testid="analytics-site-table">
        <div className="flex items-center justify-between px-6 py-4"><h2 className="text-base font-semibold md:text-lg">By site · {sum?.start} → {sum?.end}</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-6 py-2">Site</th><th className="px-3 py-2">KM</th><th className="px-3 py-2 text-right">NB vehicles</th><th className="px-3 py-2 text-right">NB km/h</th><th className="px-3 py-2 text-right">SB vehicles</th><th className="px-3 py-2 text-right">SB km/h</th><th className="px-6 py-2 text-right">Total</th></tr>
            </thead>
            <tbody>
              {(sum?.sites || []).map((r) => (
                <tr key={r.site} className="border-t border-border/60 hover:bg-secondary/40" data-testid={`site-row-${r.site}`}>
                  <td className="px-6 py-2 font-medium">{r.name} <span className="num text-xs text-muted-foreground">{r.site}</span></td>
                  <td className="num px-3 py-2 text-muted-foreground">{r.km ?? "—"}</td>
                  <td className="num px-3 py-2 text-right">{fmtNum(r.NB.n)}</td><td className="num px-3 py-2 text-right">{r.NB.spd}</td>
                  <td className="num px-3 py-2 text-right">{fmtNum(r.SB.n)}</td><td className="num px-3 py-2 text-right">{r.SB.spd}</td>
                  <td className="num px-6 py-2 text-right font-semibold">{fmtNum(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
